import { type FC, useState } from "react";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import type { EvalScoringDimension } from "@api/rest/types-and-hooks";
import { useUpsertEvalCriterion } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

/** The 1-5 scale, as strings because that is how the API keys them. */
const LEVELS = ["1", "2", "3", "4", "5"] as const;

interface CriterionDialogProps {
  /**
   * Always true. The parent mounts this only while open, with a `key` per
   * subject, so switching subjects remounts and the fields reset — rather
   * than an effect copying props into state, which React rightly flags.
   */
  open: true;
  /** Present when rewording an existing criterion; absent when adding one. */
  row?: EvalScoringDimension;
  onClose: () => void;
  onSaved: () => void;
}

export const CriterionDialog: FC<CriterionDialogProps> = ({ open, row, onClose, onSaved }) => {
  const upsert = useUpsertEvalCriterion();

  // The label for either kind, not just added ones. A default's name input
  // isn't shown, but the value still gets submitted — sending "" blanked the
  // name the judge prompt renders as the criterion's heading.
  const [name, setName] = useState(() => row?.label ?? "");
  // Prefilled from what the row already means, so editing starts from the
  // current wording rather than from blank boxes beside it.
  const [levels, setLevels] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      LEVELS.map((level) => [level, String((row?.level_anchors as Record<string, string> | undefined)?.[level] ?? "")]),
    ),
  );

  const described = LEVELS.filter((level) => levels[level]?.trim()).length;
  const isNew = !row;
  // Whether this dialog is showing the name field, and so whether a name has
  // to be there to save. A default criterion's name is fixed and not shown.
  const needsName = isNew || !row?.is_builtin;

  const submit = async () => {
    try {
      const result = await upsert.mutateAsync({
        data: {
          // Empty when adding, which the endpoint reads as "no key yet" and
          // mints one from the name. Sent rather than omitted because the
          // generated client types the field as required; the endpoint's
          // `data["dimension"] or None` is the contract, pinned by a test.
          dimension: row?.dimension ?? "",
          name,
          level_anchors: levels,
          criterion_id: row?.criterion_id ?? null,
        },
      });
      if (result.status === 200) {
        notifySuccess(isNew ? "Criteria added." : "Criteria updated.");
        onSaved();
        onClose();
      } else {
        const detail = (result.data as { detail?: string } | undefined)?.detail;
        notifyError(detail ?? `Could not save (status ${result.status}).`);
      }
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Could not save.");
    }
  };

  return (
    <Dialog
      open={open}
      setOpen={(next: boolean) => {
        if (!next) onClose();
      }}
      title={isNew ? "New criteria" : `Edit ${row.label}`}
      // Wider than the default, because a level is a sentence and five of
      // them stacked in a narrow column is unreadable.
      size="2xl"
      primaryButton={{
        text: upsert.isPending ? "Saving…" : isNew ? "Add" : "Save",
        onClick: submit,
        // A name is required whenever the field is shown, not only when
        // adding. The input appears for *added* criteria, where isNew is
        // false — so clearing "SIB tracking" and saving used to store an
        // empty name, and since keys are immutable the row became
        // permanently "Sib tracking" with no way back.
        disabled: upsert.isPending || described === 0 || (needsName && !name.trim()),
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
    >
      <div className="flex flex-col gap-y-4">
        {row?.is_builtin ? (
          <p className="text-xs text-text-tertiary">
            A default criterion — its name is fixed. Set what each score means below.
          </p>
        ) : (
          <div>
            <label htmlFor="criterion-name" className="mb-1 block text-xs font-medium text-text-secondary">
              Name
            </label>
            <input
              id="criterion-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Safety language"
              className="w-full rounded-md border border-border-medium bg-bg-surface px-3 py-2 text-sm text-text-primary outline-none focus:border-border-focus"
            />
          </div>
        )}

        <div>
          {/* The "you don't need all five" point is carried by the hint and
              the optional placeholders rather than a paragraph explaining
              itself underneath. */}
          <div className="mb-1 flex items-baseline justify-between gap-x-3">
            <span className="text-xs font-medium text-text-secondary">What each score means</span>
            <span className="text-xs text-text-tertiary">1, 3 and 5 are enough</span>
          </div>
          {/* Textareas, not inputs. A level is a sentence — "covers what is
              required; slightly thin or slightly padded" — and a single-line
              input clipped it, so you could not read what you were editing.
              Two rows each, growing to fit what is there. */}
          <div className="flex flex-col gap-y-2">
            {LEVELS.map((level) => (
              <div key={level} className="flex items-start gap-x-3">
                <span className="w-4 shrink-0 pt-1.5 text-sm text-text-secondary">{level}</span>
                <textarea
                  value={levels[level] ?? ""}
                  onChange={(event) => setLevels((prev) => ({ ...prev, [level]: event.target.value }))}
                  rows={Math.min(4, Math.max(2, Math.ceil((levels[level]?.length ?? 0) / 52)))}
                  placeholder={level === "1" ? "worst case" : level === "5" ? "best case" : "optional"}
                  className="w-full resize-y rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm leading-snug text-text-primary outline-none focus:border-border-focus"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};
